import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import axiosInstance from '../../utils/setupAxios';
import {
  ListDesktop,
  ListMobile,
  MobileCard,
  MobileCardTop,
  MobileCardTitle,
  MobileCardRow,
  MobileCardFooter,
} from '../common/ListMobileLayout';

const PAGE_SIZE = 15;
const DEBOUNCE_MS = 400;

const formatDateTime = (ts) => {
  if (!ts) return '-';
  const d = new Date(ts);
  return isNaN(d.getTime()) ? String(ts) : d.toLocaleString();
};

const isHttpUrl = (url) => typeof url === 'string' && /^https?:\/\//i.test(url);

function ProviderBadge({ provider }) {
  if (!provider) return '—';
  const cls =
    provider === 'google'   ? 'bg-danger'  :
    provider === 'apple'    ? 'bg-dark'    :
    provider === 'facebook' ? 'bg-primary' : 'bg-secondary';
  return <span className={`badge text-white ${cls}`}>{provider}</span>;
}

function CardImagePair({ obverse, reverse }) {
  const hasObverse = isHttpUrl(obverse);
  const hasReverse = isHttpUrl(reverse);
  if (!hasObverse && !hasReverse) return null;
  return (
    <div className="d-flex gap-1 mt-1">
      {hasObverse && (
        <a href={obverse} target="_blank" rel="noopener noreferrer">
          <img src={obverse} alt="front" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, border: '1px solid #dee2e6' }} />
        </a>
      )}
      {hasReverse && (
        <a href={reverse} target="_blank" rel="noopener noreferrer">
          <img src={reverse} alt="back" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, border: '1px solid #dee2e6' }} />
        </a>
      )}
    </div>
  );
}

function CollectionsSection({ collections }) {
  const [open, setOpen] = useState({});
  if (!collections?.length) return <p className="text-muted small px-1">No collections.</p>;

  return (
    <div className="d-flex flex-column gap-2">
      {collections.map((col) => {
        const isOpen = open[col.id] !== false;
        const cards = Array.isArray(col.user_coins) ? col.user_coins : [];
        return (
          <div key={col.id} className="border rounded">
            <div
              className="d-flex align-items-center px-3 py-2 bg-light rounded-top"
              style={{ cursor: 'pointer' }}
              onClick={() => setOpen((o) => ({ ...o, [col.id]: !isOpen }))}
            >
              <strong className="me-2">{col.name || '(unnamed)'}</strong>
              <span className="badge bg-secondary text-white me-auto">{cards.length} cards</span>
              <span className="text-muted small me-2">{formatDateTime(col.created_at)}</span>
              <span className="text-muted">{isOpen ? '▲' : '▼'}</span>
            </div>
            {isOpen && (
              <div className="table-responsive">
                <table className="table table-sm table-vcenter mb-0">
                  <thead>
                    <tr>
                      <th>Card ID</th>
                      <th>Name</th>
                      <th>Country</th>
                      <th>Images</th>
                      <th>Added</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cards.length === 0 ? (
                      <tr><td colSpan={5} className="text-muted text-center py-2">Empty collection.</td></tr>
                    ) : cards.map((c) => (
                      <tr key={c.id}>
                        <td><span className="badge bg-blue-lt text-blue">{c.coin_id}</span></td>
                        <td>{c.coin_name || '—'}</td>
                        <td>{c.coin_country ? <span className="badge bg-azure-lt text-azure">{c.coin_country}</span> : '—'}</td>
                        <td>
                          <CardImagePair obverse={c.coin_obverse_image || c.obverse_image_url} reverse={c.coin_reverse_image || c.reverse_image_url} />
                        </td>
                        <td className="text-muted small">{formatDateTime(c.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function RecentSnapsSection({ snaps }) {
  if (!snaps?.length) return <p className="text-muted small px-1">No recent snaps.</p>;
  return (
    <div className="table-responsive">
      <table className="table table-sm table-vcenter mb-0">
        <thead>
          <tr>
            <th>Snap Images</th>
            <th>Matched Cards</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {snaps.map((s) => (
            <tr key={s.id}>
              <td>
                <CardImagePair obverse={s.obverse_image_url} reverse={s.reverse_image_url} />
              </td>
              <td>
                {(s.coins_detail ?? []).map((cd) => (
                  <div key={cd.coin_id} className="d-flex align-items-center gap-2 mb-1">
                    <span className="badge bg-teal-lt text-teal">{cd.coin_id}</span>
                    {cd.coin_name && <span className="small">{cd.coin_name}</span>}
                    {cd.coin_country && <span className="badge bg-azure-lt text-azure">{cd.coin_country}</span>}
                    <CardImagePair obverse={cd.coin_obverse_image} reverse={cd.coin_reverse_image} />
                  </div>
                ))}
                {!(s.coins_detail?.length) && (s.coin_ids ?? []).map((cid) => (
                  <span key={cid} className="badge bg-teal-lt text-teal me-1">{cid}</span>
                ))}
              </td>
              <td className="text-muted small">{formatDateTime(s.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UserDetailModal({ user, onClose, onCardsDeleted }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);
  const [detail, setDetail] = useState(null);
  const [deleting, setDeleting]   = useState(false);
  const [deleteMsg, setDeleteMsg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    axiosInstance.get('/holo/users/detail', { params: { user_id: user.user_id } })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data ?? null;
        if (data) setDetail(data);
        else setError(res?.data?.message || 'User not found');
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Failed to load');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user.user_id]);

  const reload = () => {
    setLoading(true);
    setError(null);
    axiosInstance.get('/holo/users/detail', { params: { user_id: user.user_id } })
      .then((res) => {
        const data = res?.data?.data ?? null;
        if (data) setDetail(data);
        else setError(res?.data?.message || 'User not found');
      })
      .catch((e) => setError(e?.response?.data?.message || e?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(`Delete all cards for user ${user.user_id}? This cannot be undone.`)) return;
    setDeleting(true);
    setDeleteMsg(null);
    try {
      const res = await axiosInstance.post('/holo/users/delete-all', { user_id: user.user_id });
      setDeleteMsg({ type: 'success', text: res?.data?.message || 'All cards deleted.' });
      reload();
      if (onCardsDeleted) onCardsDeleted();
    } catch (e) {
      setDeleteMsg({ type: 'danger', text: e?.response?.data?.message || e?.message || 'Delete failed' });
    } finally {
      setDeleting(false);
    }
  };

  const cardsData = detail?.coins ?? {};
  const collections   = Array.isArray(cardsData.collections)   ? cardsData.collections   : [];
  const recentSnaps   = Array.isArray(cardsData.recent_snaps)  ? cardsData.recent_snaps  : [];
  const favoriteCards = Array.isArray(cardsData.favorite_coins) ? cardsData.favorite_coins : [];

  return ReactDOM.createPortal(
    <>
      <div className="modal-backdrop fade show" onClick={onClose} />
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
          <div className="modal-content">

            <div className="modal-header">
              <div>
                <h5 className="modal-title mb-0">{user.name || `User #${user.user_id}`}</h5>
                <div className="text-muted small mt-1 d-flex align-items-center gap-2">
                  {user.email && <span>{user.email}</span>}
                  <ProviderBadge provider={user.provider} />
                </div>
              </div>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>

            <div className="modal-body">
              {deleteMsg && (
                <div className={`alert alert-${deleteMsg.type} alert-dismissible mb-3`} role="alert">
                  {deleteMsg.text}
                  <button type="button" className="btn-close" onClick={() => setDeleteMsg(null)} />
                </div>
              )}

              {loading && (
                <div className="text-center py-5"><div className="spinner-border text-muted" role="status" /></div>
              )}

              {error && <div className="alert alert-danger">{error}</div>}

              {detail && !loading && (
                <>
                  {/* Summary */}
                  <div className="d-flex flex-wrap gap-4 mb-4 p-3 bg-light rounded">
                    <div>
                      <div className="text-muted small">User ID</div>
                      <code>{user.user_id}</code>
                    </div>
                    <div>
                      <div className="text-muted small">Total cards</div>
                      <strong>{detail.coins_count ?? 0}</strong>
                    </div>
                    <div>
                      <div className="text-muted small">Collections</div>
                      <strong>{collections.length}</strong>
                    </div>
                    <div>
                      <div className="text-muted small">Recent snaps</div>
                      <strong>{recentSnaps.length}</strong>
                    </div>
                    <div>
                      <div className="text-muted small">Registered</div>
                      <span>{formatDateTime(detail.created_at)}</span>
                    </div>
                    <div>
                      <div className="text-muted small">Last updated</div>
                      <span>{formatDateTime(detail.updated_at)}</span>
                    </div>
                  </div>

                  {/* Collections */}
                  <h6 className="mb-2">Collections</h6>
                  <div className="mb-4">
                    <CollectionsSection collections={collections} />
                  </div>

                  {/* Recent Snaps */}
                  <h6 className="mb-2">Recent Snaps</h6>
                  <div className="mb-4">
                    <RecentSnapsSection snaps={recentSnaps} />
                  </div>

                  {/* Favorites */}
                  {favoriteCards.length > 0 && (
                    <>
                      <h6 className="mb-2">Favorite Cards</h6>
                      <div className="mb-2">
                        {favoriteCards.map((fc, i) => (
                          <span key={i} className="badge bg-yellow-lt text-yellow me-1">{typeof fc === 'string' ? fc : JSON.stringify(fc)}</span>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-danger btn-sm me-auto"
                onClick={handleDeleteAll}
                disabled={deleting || loading}
              >
                {deleting && <span className="spinner-border spinner-border-sm me-1" role="status" />}
                Delete all cards
              </button>
              <button className="btn btn-secondary" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

export default function Users() {
  const [searchInput, setSearchInput] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [committedDateFrom, setCommittedDateFrom] = useState('');
  const [committedDateTo, setCommittedDateTo]     = useState('');
  const [hasCollection, setHasCollection] = useState(false);
  const [hasSnap, setHasSnap]             = useState(false);
  const [hasFavourite, setHasFavourite]   = useState(false);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const debounceRef = useRef(null);

  const fetchUsers = useCallback(async (q, from, to, p, collection, snap, favourite) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: p, per_page: PAGE_SIZE };
      if (q)          params.search         = q;
      if (from)       params.date_from      = from;
      if (to)         params.date_to        = to;
      if (collection) params.has_collection = 1;
      if (snap)       params.has_coin_snap  = 1;
      if (favourite)  params.has_favourite  = 1;
      const res = await axiosInstance.get('/holo/users', { params });
      const payloadData = res?.data?.data ?? {};
      const rows    = Array.isArray(payloadData.items) ? payloadData.items : [];
      const objects = payloadData.objects ?? {};
      const t = objects.total ?? objects.count ?? rows.length;
      setItems(rows);
      setTotal(t);
      setLastPage(Math.max(1, Math.ceil(t / PAGE_SIZE)));
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  const handleDateFrom = (v) => { setDateFrom(v); setCommittedDateFrom(v); setPage(1); };
  const handleDateTo   = (v) => { setDateTo(v);   setCommittedDateTo(v);   setPage(1); };

  useEffect(() => {
    fetchUsers(search, committedDateFrom, committedDateTo, page, hasCollection, hasSnap, hasFavourite);
  }, [search, committedDateFrom, committedDateTo, page, hasCollection, hasSnap, hasFavourite, fetchUsers]);

  const handleClear = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchInput(''); setDateFrom(''); setDateTo('');
    setSearch(''); setCommittedDateFrom(''); setCommittedDateTo('');
    setHasCollection(false); setHasSnap(false); setHasFavourite(false);
    setPage(1);
  };

  const hasFilters = searchInput || dateFrom || dateTo || hasCollection || hasSnap || hasFavourite;

  const pageNumbers = () => {
    const start = Math.max(1, Math.min(page - 2, lastPage - 4));
    const end   = Math.min(lastPage, start + 4);
    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const PaginationBar = () => (
    <div className="d-flex align-items-center px-3 py-2">
      <p className="m-0 text-muted small">
        Page <strong>{page}</strong> / <strong>{lastPage}</strong>
        {' · '}{(total ?? 0).toLocaleString()} total
      </p>
      {lastPage > 1 && (
        <ul className="pagination pagination-sm m-0 ms-auto">
          <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => setPage((p) => Math.max(1, p - 1))}>‹</button>
          </li>
          {pageNumbers().map((n) => (
            <li key={n} className={`page-item ${n === page ? 'active' : ''}`}>
              <button className="page-link" onClick={() => setPage(n)}>{n}</button>
            </li>
          ))}
          <li className={`page-item ${page >= lastPage ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => setPage((p) => Math.min(lastPage, p + 1))}>›</button>
          </li>
        </ul>
      )}
    </div>
  );

  return (
    <div className="dashboard-list-page">
      <div className="page-header d-print-none mb-3">
        <div className="row align-items-center">
          <div className="col">
            <h2 className="page-title">Users</h2>
            <div className="text-muted mt-1">Manage users and their synced card collections.</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-body py-3">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-sm-4">
              <label className="form-label mb-1">Search</label>
              <div className="input-group">
                <input
                  className="form-control"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Name, email, user ID…"
                />
                {searchInput && (
                  <button className="btn btn-outline-secondary" type="button" onClick={() => setSearchInput('')}>&times;</button>
                )}
              </div>
            </div>
            <div className="col-6 col-sm-3">
              <label className="form-label mb-1">Registered from</label>
              <input type="date" className="form-control" value={dateFrom} onChange={(e) => handleDateFrom(e.target.value)} />
            </div>
            <div className="col-6 col-sm-3">
              <label className="form-label mb-1">Registered to</label>
              <input type="date" className="form-control" value={dateTo} onChange={(e) => handleDateTo(e.target.value)} />
            </div>
            <div className="col-12 col-sm-auto d-flex align-items-end gap-3 flex-wrap">
              <div className="form-check mb-1">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="filter-collection"
                  checked={hasCollection}
                  onChange={(e) => { setHasCollection(e.target.checked); setPage(1); }}
                />
                <label className="form-check-label" htmlFor="filter-collection">Has collection</label>
              </div>
              <div className="form-check mb-1">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="filter-snap"
                  checked={hasSnap}
                  onChange={(e) => { setHasSnap(e.target.checked); setPage(1); }}
                />
                <label className="form-check-label" htmlFor="filter-snap">Has snap</label>
              </div>
              <div className="form-check mb-1">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="filter-favourite"
                  checked={hasFavourite}
                  onChange={(e) => { setHasFavourite(e.target.checked); setPage(1); }}
                />
                <label className="form-check-label" htmlFor="filter-favourite">Has favourite</label>
              </div>
            </div>
            {hasFilters && (
              <div className="col-auto">
                <button className="btn btn-outline-secondary" onClick={handleClear}>Clear filters</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="list-pagination-top">
          <PaginationBar />
        </div>
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border spinner-border-sm text-muted" role="status" />
          </div>
        ) : error ? (
          <div className="text-center text-danger py-4">{error}</div>
        ) : items.length === 0 ? (
          <div className="text-center text-muted py-4">No users found.</div>
        ) : (
          <>
            <ListDesktop>
              <table className="table table-vcenter card-table mb-0">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>User ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Provider</th>
                    <th>Cards</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((u) => (
                    <tr key={u.id}>
                      <td className="text-muted small">{u.id}</td>
                      <td><code className="small">{u.user_id}</code></td>
                      <td>{u.name || '—'}</td>
                      <td className="text-muted small">{u.email || '—'}</td>
                      <td><ProviderBadge provider={u.provider} /></td>
                      <td>{u.coins_count ?? '-'}</td>
                      <td className="text-muted small">{formatDateTime(u.created_at)}</td>
                      <td>
                        <button className="btn btn-sm btn-outline-primary" onClick={() => setSelectedUser(u)}>
                          Detail
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ListDesktop>

            <ListMobile>
              {items.map((u) => (
                <MobileCard key={u.id}>
                  <MobileCardTop>
                    <MobileCardTitle>{u.name || u.email || `User #${u.id}`}</MobileCardTitle>
                    <ProviderBadge provider={u.provider} />
                  </MobileCardTop>
                  <MobileCardRow label="ID">{u.id}</MobileCardRow>
                  <MobileCardRow label="User ID" mono>{u.user_id}</MobileCardRow>
                  <MobileCardRow label="Email">{u.email || '—'}</MobileCardRow>
                  <MobileCardRow label="Cards">{u.coins_count ?? '—'}</MobileCardRow>
                  <MobileCardRow label="Created">{formatDateTime(u.created_at)}</MobileCardRow>
                  <MobileCardFooter>
                    <button className="btn btn-sm btn-outline-primary" onClick={() => setSelectedUser(u)}>
                      Detail
                    </button>
                  </MobileCardFooter>
                </MobileCard>
              ))}
            </ListMobile>
          </>
        )}
        <PaginationBar />
      </div>

      {selectedUser && (
        <UserDetailModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onCardsDeleted={() => fetchUsers(search, committedDateFrom, committedDateTo, page, hasCollection, hasSnap, hasFavourite)}
        />
      )}
    </div>
  );
}
